import React, { useState, useEffect } from 'react';
import { renderMathContent } from '../../utils/renderMath';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { examData, loadExamSession } from '../../data/examData';
import { useAuth } from '../../contexts/AuthContext';
import LoginPromptManager from '../auth/LoginPromptManager';
import useAuthPrompt from '../../hooks/useAuthPrompt';
import { saveWrongAnswer } from '../../utils/wrongAnswerStorage';
import { updateQuestionProgress, updateSessionComplete } from '../../utils/learningProgressStorage';
import { recordPracticeResult } from '../../utils/practiceAchievementStorage';
import { gamificationActions } from '../../utils/gamificationSystem';
import { getEnglishKey } from '../../utils/koreanNameMapper';
import { canonicalSubject, groupBySubject } from '../../utils/subjectNormalizer';
import Header from '../common/Header';

// 진단평가용 이미지 경로 생성 함수
// 이미지 src 추출 (객체면 .src, 문자열이면 그대로) — 데이터 스키마 2종 모두 대응
function imgSrcOf(question) {
  const img = question && question.image;
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (typeof img === 'object' && img.src) return img.src;
  return null;
}

function getImagePath(question) {
  const rawSrc = imgSrcOf(question);
  if (!rawSrc) {
    return null;
  }

  const cleanImageSrc = rawSrc.replace(/^images\//, '').replace(/^\//, '');

  // 1. 출처 정보(회차)로 CBT와 동일한 정확 경로를 우선 생성한다.
  //    저장된 imagePath에는 'session-N회'(한글 회 접미사) 등 버그가 섞여 있어
  //    출처정보가 있으면 그쪽을 신뢰한다. (모의/과목별 이미지 깨짐 재발방지)
  const sourceInfo = {
    examType: question.examType || question.source?.examType,
    year: question.year || question.source?.year,
    session: question.session || question.source?.session
  };
  if (sourceInfo.examType && sourceInfo.year && sourceInfo.session) {
    return `/images/${sourceInfo.examType}/${sourceInfo.year}/session-${sourceInfo.session}/${cleanImageSrc}`;
  }

  // 2. 출처 정보가 없을 때만 저장된 imagePath 사용
  if (question.imagePath) {
    return question.imagePath;
  }
  
  // 3. 출처 정보가 전혀 없을 때의 최후 폴백
  return `/images/${cleanImageSrc}`;
}

const DiagnosticTest = () => {
  const { examSlug: examType } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 모드 파라미터: count(문항수), subject(특정 과목 집중). 없으면 기존 진단평가(20문항 전과목).
  const count = Math.min(parseInt(searchParams.get('count') || '20', 10) || 20, 200);
  const focusSubject = searchParams.get('subject') || null;
  const pickMode = searchParams.get('pick') === '1';  // 과목 선택 화면
  const { isAuthenticated, user } = useAuth();
  const { 
    userMetrics, 
    promptConfig, 
    trackQuestionSolved, 
    dismissPrompt 
  } = useAuthPrompt(isAuthenticated);

  // 상태 관리
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timePerQuestion, setTimePerQuestion] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [testStartTime] = useState(Date.now());
  const [diagnosticQuestions, setDiagnosticQuestions] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState(null);  // 과목 선택용
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(false);
  const [time, setTime] = useState(count * 60); // 문항당 1분 (20문항=20분, 100문항=100분)
  const [showResults, setShowResults] = useState({}); // 각 문제별 정답 표시 상태
  const [isAnswerLocked, setIsAnswerLocked] = useState({}); // 답변 잠금 상태
  const [showAIExplanation, setShowAIExplanation] = useState({}); // 각 문제별 AI 해설 표시 상태
  const [currentStreak, setCurrentStreak] = useState(0); // 현재 연승

  // 수식 렌더링: 공용 유틸 사용 (src/utils/renderMath.jsx) — 일반풀이와 동일 로직

  // Helper functions
  const getAnsweredCount = () => {
    return Object.keys(selectedAnswers).length;
  };

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // 타이머
  useEffect(() => {
    if (time > 0 && !result) {
      const timer = setInterval(() => setTime(t => t - 1), 1000);
      return () => clearInterval(timer);
    } else if (time === 0 && !result) {
      handleTestComplete();
    }
  }, [time, result]);

  // 진단평가용 문제 선택 (20문제)
  useEffect(() => {
    const selectDiagnosticQuestions = async () => {
      try {
        // 한글 자격증명을 영문 키로 변환
        const englishExamType = getEnglishKey(examType);
        const exam = examData[englishExamType];
        
        if (!exam || !exam.years) {
          console.error('❌ Invalid exam data:', { 
            originalExamType: examType, 
            convertedExamType: englishExamType, 
            exam 
          });
          setIsLoading(false);
          return;
        }

        const allQuestions = [];
        
        // 모든 연도의 문제를 동적으로 로드
        for (const [yearKey, yearData] of Object.entries(exam.years)) {
          if (yearData.sessions) {
            for (const [sessionKey] of Object.entries(yearData.sessions)) {
              try {
                const yNum = parseInt(yearKey), sNum = parseInt(sessionKey);
                const sessionData = await loadExamSession(englishExamType, yNum, sNum);
                if (sessionData && sessionData.questions) {
                  // 원본 회차 정보를 각 문항에 태깅 (모의고사/과목별에서 이미지 경로 복원용)
                  allQuestions.push(...sessionData.questions.map(q => ({
                    ...q, examType: englishExamType, year: yNum, session: sNum,
                  })));
                }
              } catch (error) {
                // 세션 로드 실패 시 무시하고 계속 진행
                continue;
              }
            }
          }
        }

        // pick 모드: 공식 과목으로 정규화한 목록 계산 후 선택 화면 표시
        if (pickMode && !focusSubject) {
          setAvailableSubjects(groupBySubject(allQuestions, englishExamType));
          setIsLoading(false);
          return;
        }

        // 출제 문제 선택: focusSubject 지정 시 해당 과목만, 아니면 전과목 균등
        let selectedQuestions = [];
        if (focusSubject) {
          // 과목 집중 모드: 정규화된 공식 과목으로 매칭해 해당 과목 랜덤 count개
          const pool = allQuestions.filter(q =>
            canonicalSubject(englishExamType, q.subject || q.category) === focusSubject
          );
          selectedQuestions = [...pool].sort(() => Math.random() - 0.5).slice(0, count);
        } else {
          // 전과목 균등 분배 (진단평가/실전 모의고사 공통)
          const subjects = (exam.subjects && exam.subjects.length)
            ? exam.subjects
            : [...new Set(allQuestions.map(q => q.subject).filter(Boolean))];
          const per = Math.floor(count / (subjects.length || 4));
          subjects.forEach(subject => {
            const sq = allQuestions.filter(q => q.subject === subject || q.category === subject);
            selectedQuestions.push(...[...sq].sort(() => Math.random() - 0.5).slice(0, per));
          });
          // 부족분 랜덤 보충 (중복 제외)
          const picked = new Set(selectedQuestions);
          const rest = allQuestions.filter(q => !picked.has(q)).sort(() => Math.random() - 0.5);
          while (selectedQuestions.length < count && rest.length) selectedQuestions.push(rest.pop());
        }

        // 최종 count문제로 제한하고 섞기
        const finalQuestions = selectedQuestions
          .slice(0, count)
          .sort(() => Math.random() - 0.5)
          .map((q, index) => ({
            ...q,
            questionNumber: index + 1
          }));

        setDiagnosticQuestions(finalQuestions);
        setIsLoading(false);
        
        // Google Analytics 시험 시작 이벤트
        if (window.trackExamStart && finalQuestions.length > 0) {
          window.trackExamStart('diagnostic', 'mixed', 'assessment');
        }
      } catch (error) {
        console.error('❌ Error selecting diagnostic questions:', error);
        setIsLoading(false);
      }
    };

    selectDiagnosticQuestions();
  }, [examType, count, focusSubject, pickMode]);

  // 진행률 저장
  useEffect(() => {
    const saveProgress = () => {
      const progress = {
        examType,
        currentQuestion,
        selectedAnswers,
        timePerQuestion,
        testStartTime,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('diagnosticProgress', JSON.stringify(progress));
    };

    if (diagnosticQuestions.length > 0 && currentQuestion >= 0) {
      saveProgress();
    }
  }, [currentQuestion, selectedAnswers, examType, testStartTime, diagnosticQuestions.length]);

  // 이탈 방지 - beforeunload 이벤트 및 세션 데이터 저장
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentQuestion > 0 || Object.keys(selectedAnswers).length > 0) {
        // 진행 중인 세션 데이터 저장
        const userId = user?.id || 'guest';
        const sessionTime = Math.round((Date.now() - testStartTime) / 1000 / 60); // 분 단위
        const answeredCount = Object.keys(selectedAnswers).length;
        const correctCount = Object.entries(selectedAnswers).reduce((acc, [qId, answer]) => {
          const question = diagnosticQuestions.find(q => q.id === qId);
          return acc + (question && question.correctAnswer === answer ? 1 : 0);
        }, 0);
        
        updateSessionComplete(userId, {
          sessionTime,
          questionsAnswered: answeredCount,
          correctAnswers: correctCount,
          examType: `diagnostic_${getEnglishKey(examType)}`,
          completedExam: false // 중간에 나간 경우
        });
        
        e.preventDefault();
        e.returnValue = '진단평가가 진행 중입니다. 정말 나가시겠습니까?';
        return e.returnValue;
      }
    };

    const handleUnload = () => {
      // 컴포넌트 언마운트 시에도 데이터 저장
      if (currentQuestion > 0 || Object.keys(selectedAnswers).length > 0) {
        const userId = user?.id || 'guest';
        const sessionTime = Math.round((Date.now() - testStartTime) / 1000 / 60);
        const answeredCount = Object.keys(selectedAnswers).length;
        const correctCount = Object.entries(selectedAnswers).reduce((acc, [qId, answer]) => {
          const question = diagnosticQuestions.find(q => q.id === qId);
          return acc + (question && question.correctAnswer === answer ? 1 : 0);
        }, 0);
        
        updateSessionComplete(userId, {
          sessionTime,
          questionsAnswered: answeredCount,
          correctAnswers: correctCount,
          examType: `diagnostic_${getEnglishKey(examType)}`,
          completedExam: false
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleUnload();
    };
  }, [currentQuestion, selectedAnswers, diagnosticQuestions, examType, testStartTime, user?.id]);

  // Keyboard navigation (CBT와 동일)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (currentQuestion > 0) {
            handlePrevious();
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (currentQuestion < diagnosticQuestions.length - 1) {
            handleNext();
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (currentQuestion === diagnosticQuestions.length - 1) {
            if (window.confirm(`총 ${diagnosticQuestions.length}문제 중 ${getAnsweredCount()}문제에 답변하셨습니다.\n\n진단평가를 완료하시겠습니까?`)) {
              handleTestComplete();
            }
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentQuestion, diagnosticQuestions]);

  // 답변 선택 (CBT와 동일한 로직)
  const handleAnswerSelect = (questionIndex, answerIndex) => {
    const question = diagnosticQuestions[questionIndex];
    if (!question) return;
    
    // 이미 답변한 문제는 변경 불가
    if (isAnswerLocked[questionIndex]) return;
    
    // 답변 선택 (1-based indexing으로 저장)
    const selectedOptionNumber = answerIndex + 1;
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: selectedOptionNumber
    }));
    
    // 즉시 정답 공개 (AI 해설은 제외)
    setTimeout(() => {
      setShowResults(prev => ({...prev, [questionIndex]: true}));
      setIsAnswerLocked(prev => ({...prev, [questionIndex]: true}));
      
      // 정답인 경우와 오답인 경우 처리 (AI 해설은 즉시 표시하지 않음)
      if (question.correctAnswer === selectedOptionNumber) {
        // 정답 효과 (나중에 추가)
      } else {
        // 오답일 때도 AI 해설은 사용자가 선택할 때까지 숨김
      }
    }, 300); // 300ms 딜레이 후 결과 표시
  };

  // 다음 문제로 이동
  const handleNext = () => {
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setTimePerQuestion(prev => ({
      ...prev,
      [currentQuestion]: timeSpent
    }));

    const question = diagnosticQuestions[currentQuestion];
    const userAnswer = selectedAnswers[currentQuestion];
    const isCorrect = userAnswer === question?.correctAnswer;
    
    trackQuestionSolved(isCorrect);

    // 로그인한 사용자의 경우 오답노트에 자동 저장
    if (!isCorrect && isAuthenticated && user?.id && question) {
      const wrongAnswerData = {
        questionId: `diagnostic_${examType}_${currentQuestion}_${Date.now()}`,
        questionText: question.questionText,
        options: question.options,
        correctAnswer: question.correctAnswer,
        userAnswer: userAnswer,
        explanation: question.explanation,
        examType: examType + '_진단평가',
        subject: question.subject || examType,
        difficulty: question.difficulty,
        image: question.image,
        tags: ['진단평가', ...(question.tags || [])],
        year: new Date().getFullYear(), // 진단평가는 현재 연도로 설정
        session: '진단', // 진단평가는 '진단'으로 설정
        questionNumber: currentQuestion + 1 // 문제 번호 추가 (1부터 시작)
      };
      
      saveWrongAnswer(user.id, wrongAnswerData);
    }

    // 연승 업데이트
    if (isCorrect) {
      setCurrentStreak(prev => prev + 1);
    } else {
      setCurrentStreak(0);
    }

    // 학습 진도 업데이트
    if (isAuthenticated && user?.id && question) {
      updateQuestionProgress(user.id, {
        examType: `diagnostic_${getEnglishKey(examType)}`, // diagnostic_ 접두사로 통일
        subject: question.subject || getEnglishKey(examType),
        isCorrect: isCorrect,
        timeSpent: timeSpent,
        sessionId: 'diagnostic_' + Date.now().toString(),
        totalQuestions: diagnosticQuestions.length // 총 문제수 정보 추가
      });
    }

    // 게이미피케이션 기록
    if (isCorrect && isAuthenticated && user?.id) {
      const userId = user.id;
      const newStreak = isCorrect ? currentStreak + 1 : 0;
      gamificationActions.recordCorrectAnswer(newStreak, userId);
    }

    if (currentQuestion < diagnosticQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setQuestionStartTime(Date.now());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleTestComplete();
    }
  };

  // 이전 문제로 이동
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setQuestionStartTime(Date.now());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // 테스트 완료 처리
  const handleTestComplete = () => {
    const totalTime = Math.floor((Date.now() - testStartTime) / 1000);
    
    // 점수 계산 및 오답노트 저장 (1-based indexing 처리)
    let correctCount = 0;
    diagnosticQuestions.forEach((question, index) => {
      const userAnswer = selectedAnswers[index];
      const isCorrect = userAnswer === question.correctAnswer;
      
      if (isCorrect) {
        correctCount++;
      } else if (isAuthenticated && user?.id && question && userAnswer !== undefined) {
        // 오답인 경우 오답노트에 저장
        const wrongAnswerData = {
          questionId: `diagnostic_${examType}_${index}_${Date.now()}`,
          questionText: question.questionText,
          options: question.options,
          correctAnswer: question.correctAnswer,
          userAnswer: userAnswer,
          explanation: question.explanation,
          examType: examType + '_진단평가',
          subject: question.subject || examType,
          difficulty: question.difficulty,
          image: question.image,
          tags: ['진단평가', ...(question.tags || [])],
          year: new Date().getFullYear(), // 진단평가는 현재 연도로 설정
          session: '진단', // 진단평가는 '진단'으로 설정
          questionNumber: index + 1 // 문제 번호 추가 (1부터 시작)
        };
        
        saveWrongAnswer(user.id, wrongAnswerData);
      }
    });

    const score = Math.round((correctCount / diagnosticQuestions.length) * 100);
    
    // 과목별 점수 계산
    const subjectScores = {};
    const exam = examData[getEnglishKey(examType)];
    
    exam.subjects?.forEach(subject => {
      const subjectQuestions = diagnosticQuestions.filter(q => 
        q.subject === subject || q.category === subject
      );
      const subjectCorrect = subjectQuestions.filter((q, idx) => {
        const qIndex = diagnosticQuestions.indexOf(q);
        return selectedAnswers[qIndex] === q.correctAnswer;
      }).length;
      
      subjectScores[subject] = {
        correct: subjectCorrect,
        total: subjectQuestions.length,
        percentage: subjectQuestions.length > 0 
          ? Math.round((subjectCorrect / subjectQuestions.length) * 100)
          : 0
      };
    });

    const avgTimePerQuestion = Math.round(totalTime / diagnosticQuestions.length);

    const results = {
      score,
      correctCount,
      totalQuestions: diagnosticQuestions.length,
      totalTime,
      avgTimePerQuestion,
      subjectScores,
      timePerQuestion,
      selectedAnswers,
      examType,
      timestamp: new Date().toISOString()
    };

    // 과목별 정답 집계 (공식 과목으로 정규화, 답변한 문항 기준)
    const englishExamType = getEnglishKey(examType);
    const subjectBreakdown = {};
    diagnosticQuestions.forEach((q, index) => {
      if (selectedAnswers[index] === undefined) return; // 미답변 제외
      const subj = canonicalSubject(englishExamType, q.subject || q.category) || focusSubject || '기타';
      if (!subjectBreakdown[subj]) subjectBreakdown[subj] = { correct: 0, total: 0 };
      subjectBreakdown[subj].total += 1;
      if (selectedAnswers[index] === q.correctAnswer) subjectBreakdown[subj].correct += 1;
    });

    // 학습 세션 완료 업데이트
    if (isAuthenticated && user?.id) {
      const userId = user.id;
      updateSessionComplete(userId, {
        sessionTime: Math.round(totalTime / 60), // 분 단위로 변환
        questionsAnswered: diagnosticQuestions.length,
        correctAnswers: correctCount,
        examType: examType + '_진단평가',
        completedExam: true
      });

      // 모의고사 / 과목별 연습 성취도 기록 (진단 통계와 별도 저장)
      recordPracticeResult(userId, {
        mode: (!focusSubject && count >= 50) ? 'mock' : 'subject',
        cert: englishExamType,
        certTitle: examData[englishExamType]?.title || examType,
        focusSubject,
        score,
        correctCount,
        totalQuestions: diagnosticQuestions.length,
        totalTime,
        subjectBreakdown,
      });

      // 게이미피케이션 시험 완료 기록
      gamificationActions.recordExamCompletion(score, userId);
    }

    localStorage.setItem('diagnosticResults', JSON.stringify(results));
    localStorage.removeItem('diagnosticProgress');
    
    // Google Analytics 시험 완료 이벤트
    if (window.trackExamComplete) {
      window.trackExamComplete('diagnostic', correctCount, diagnosticQuestions.length);
    }
    
    setResult(true);
    navigate(`/diagnostic/${examType}/results`, { state: { results } });
  };

  // 진행률 계산
  const progress = ((currentQuestion + 1) / diagnosticQuestions.length) * 100;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-orange-500 mx-auto mb-6"></div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-4">진단평가 문제를 준비하고 있습니다</h2>
          <p className="text-gray-600 text-lg">각 과목에서 문제를 선별하고 있습니다...</p>
          <div className="mt-6 flex justify-center space-x-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
        </div>
      </div>
    );
  }

  // 과목 선택 화면 (pick 모드)
  if (pickMode && !focusSubject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 text-gray-900">
        <Header setShowLoginPrompt={() => {}} />
        <div className="max-w-3xl mx-auto pt-24 pb-10 px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">과목별 집중 연습</h2>
            <button onClick={() => navigate(`/${examType}`)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-7 h-7" /></button>
          </div>
          <p className="text-gray-600 mb-6">집중적으로 풀고 싶은 과목을 선택하세요. 해당 과목에서 랜덤으로 {count}문항이 출제됩니다.</p>
          {(!availableSubjects || availableSubjects.length === 0) ? (
            <div className="bg-white rounded-2xl p-8 text-center shadow-lg border border-orange-100">
              <p className="text-gray-500">이 자격증은 아직 과목 태그가 없어 과목별 연습을 제공할 수 없습니다.<br/>전체 랜덤 모의고사를 이용해 주세요.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {availableSubjects.map(({ subject: subj, count: cnt }) => (
                <button
                  key={subj}
                  onClick={() => navigate(`/diagnostic/${examType}?subject=${encodeURIComponent(subj)}&count=${Math.min(count, cnt)}`)}
                  className="bg-white hover:bg-orange-50 border border-orange-200 rounded-2xl p-5 text-left shadow-sm hover:shadow-md transition-all flex items-center justify-between group"
                >
                  <span className="text-lg font-semibold text-gray-800">{subj}</span>
                  <span className="text-orange-500 font-medium">→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Result state
  if (result) {
    const finalScore = Math.round((Object.keys(selectedAnswers).filter(key => 
      selectedAnswers[key] === diagnosticQuestions[key]?.correctAnswer
    ).length / diagnosticQuestions.length) * 100);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 text-gray-900 relative overflow-hidden">
        <div className="relative z-20 min-h-screen py-8">
          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 text-center border border-orange-200">
              <div className="mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">실력 진단 완료</h2>
                <div className="text-7xl font-bold mb-6 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent animate-pulse">{finalScore}점</div>
                <div className="bg-orange-50 rounded-2xl p-6 mb-6 border border-orange-200">
                  <p className="text-xl font-semibold mb-2 bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent">
                    {finalScore >= 80 ? '🎉 훌륭합니다! 실력이 뛰어나시네요.' :
                     finalScore >= 60 ? '👍 좋습니다! 조금만 더 노력하면 합격권입니다.' :
                     finalScore >= 40 ? '📚 기초는 갖추셨네요. 더 공부가 필요합니다.' :
                     '💪 기초부터 차근차근 공부하시면 좋겠습니다.'}
                  </p>
                </div>
              </div>
              
              <div className="bg-orange-50 rounded-2xl p-8 mb-8 border border-orange-200">
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <span className="text-gray-600">총 문제 수:</span>
                    <span className="font-semibold ml-2">20문제</span>
                  </div>
                  <div>
                    <span className="text-gray-600">답변한 문제:</span>
                    <span className="font-semibold ml-2">{getAnsweredCount()}문제</span>
                  </div>
                  <div>
                    <span className="text-gray-600">정답 수:</span>
                    <span className="font-semibold ml-2 text-green-600">
                      {Object.keys(selectedAnswers).filter(key => 
                        selectedAnswers[key] === diagnosticQuestions[key]?.correctAnswer
                      ).length}문제
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">오답 수:</span>
                    <span className="font-semibold ml-2 text-red-600">
                      {getAnsweredCount() - Object.keys(selectedAnswers).filter(key => 
                        selectedAnswers[key] === diagnosticQuestions[key]?.correctAnswer
                      ).length}문제
                    </span>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => window.location.reload()} 
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-12 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg"
              >
                다시 시작하기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const question = diagnosticQuestions[currentQuestion];
  if (!question) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 text-gray-900 relative grid grid-rows-[auto_1fr_auto]">
      {/* Side Navigation Arrows */}
      {currentQuestion > 0 && (
        <button
          onClick={handlePrevious}
          className="fixed left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-30 
                     bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700
                     text-white shadow-lg hover:shadow-xl
                     w-11 h-11 md:w-12 md:h-12 rounded-full
                     flex items-center justify-center
                     transition-all duration-300 hover:scale-110 active:scale-95
                     touch-manipulation"
          aria-label="이전 문제"
          title="이전 문제 (← 키)"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {currentQuestion < diagnosticQuestions.length - 1 ? (
        <button
          onClick={handleNext}
          disabled={selectedAnswers[currentQuestion] === undefined}
          className={`fixed right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-30 
                     ${selectedAnswers[currentQuestion] === undefined 
                       ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                       : 'bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'}
                     w-11 h-11 md:w-12 md:h-12 rounded-full shadow-lg hover:shadow-xl
                     flex items-center justify-center
                     transition-all duration-300 ${selectedAnswers[currentQuestion] !== undefined ? 'hover:scale-110 active:scale-95' : ''}
                     touch-manipulation`}
          aria-label="다음 문제"
          title="다음 문제 (→ 키)"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ) : (
        <button
          onClick={() => {
            if (window.confirm(`총 ${diagnosticQuestions.length}문제 중 ${getAnsweredCount()}문제에 답변하셨습니다.\n\n진단평가를 완료하시겠습니까?`)) {
              handleTestComplete();
            }
          }}
          disabled={selectedAnswers[currentQuestion] === undefined}
          className={`fixed right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-30 
                     ${selectedAnswers[currentQuestion] === undefined 
                       ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                       : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'}
                     w-11 h-11 md:w-12 md:h-12 rounded-full shadow-lg hover:shadow-xl
                     flex items-center justify-center
                     transition-all duration-300 ${selectedAnswers[currentQuestion] !== undefined ? 'hover:scale-110 active:scale-95' : ''}
                     touch-manipulation`}
          aria-label="진단평가 완료"
          title="진단평가 완료 (Enter 키)"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}

      <div className="relative z-20 h-full grid grid-rows-[auto_1fr_auto]">
        {/* Header - Fixed */}
        <header className="bg-gradient-to-r from-orange-50 to-orange-100 shadow-sm border-b border-orange-200 px-4 py-3">
          <div className="max-w-6xl mx-auto relative">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div>
                  <h1 className="text-lg font-bold text-primary-600">실력 진단평가</h1>
                  <p className="text-sm text-gray-600">{examData[getEnglishKey(examType)]?.title || examType}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className={`text-xl font-mono font-bold ${time < 300 ? 'text-red-500' : 'text-gray-800'}`}>
                  {formatTime(time)}
                </div>
                <button
                  onClick={() => navigate(`/${examType}`)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="진단평가 종료"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-gradient-to-r from-orange-500 to-orange-600 h-1.5 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>진행률: {Math.round(progress)}%</span>
                <span>답변: {getAnsweredCount()} / {diagnosticQuestions.length}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area - Scrollable */}
        <main className="overflow-y-auto">
          <div className="max-w-6xl mx-auto p-4">
            <div className="relative bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-6 mb-4 border border-orange-100">
            
              {/* 문제 번호 - 좌측 상단에 크게 걸치게 표시 */}
              <div className="absolute -top-4 -left-2 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-lg">
                Q{question.questionNumber}
              </div>
              
              {/* 문제 텍스트 - 바로 시작 */}
              <div className="text-lg leading-relaxed mb-4 text-gray-800 whitespace-pre-wrap">
                {typeof question.questionText === 'string' 
                  ? renderMathContent(question.questionText)
                  : question.questionText
                }
              </div>
              
              {/* Image if exists - 경로를 만들 수 있을 때만 렌더 */}
              {question.image && getImagePath(question) && (
                <div className="mb-3">
                  <img
                    src={getImagePath(question)}
                    alt={typeof question.image === 'object' && question.image.description || "문제 관련 이미지"}
                    className="w-auto h-auto max-w-full max-h-56 sm:max-h-72 md:max-h-80 object-contain"
                    onError={(e) => {
                      // 출처정보 기반 경로(CBT와 동일)가 실패하면 파일이 실제로 없는 것.
                      // 타 자격증 동일파일명 추측은 오히려 잘못된 이미지를 띄우므로 하지 않고 숨긴다.
                      console.warn('이미지 로드 실패:', imgSrcOf(question));
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              {/* Options */}
              <div className="space-y-2">
                {question.options?.map((option, index) => {
                  const optionNumber = index + 1;
                  const isSelected = selectedAnswers[currentQuestion] === optionNumber;
                  const optionText = typeof option === 'object' ? option.text : option;
                  
                  // 이 문제가 이미 답변되었는지 확인
                  const isQuestionAnswered = showResults[currentQuestion];
                  const isCorrectOption = question.correctAnswer === optionNumber;
                  const isWrongSelected = isQuestionAnswered && isSelected && !isCorrectOption;
                  
                  return (
                    <button
                      key={`${currentQuestion}-option-${optionNumber}`}
                      onClick={() => handleAnswerSelect(currentQuestion, index)}
                      disabled={isAnswerLocked[currentQuestion]}
                      className={`
                        relative w-full text-left p-3 pl-5 border-2 rounded-lg 
                        transition-all duration-300 transform-gpu
                        ${
                          isQuestionAnswered
                            ? isCorrectOption
                              ? 'border-green-500 bg-green-50 shadow-lg'
                              : isWrongSelected
                              ? 'border-red-500 bg-red-50 shadow-lg animate-shake'
                              : 'border-gray-300 bg-gray-100 opacity-60'
                            : isSelected 
                              ? 'border-orange-500 bg-orange-50 shadow-lg scale-100' 
                              : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-orange-400 hover:scale-[1.02] active:scale-[0.98] cursor-pointer'
                        }
                        ${isAnswerLocked[currentQuestion] ? 'cursor-not-allowed' : 'cursor-pointer'}
                        touch-manipulation
                      `}
                    >
                      {/* 선택지 번호 배지 - 왼쪽 밖으로 걸치게 */}
                      <div className={`
                        absolute -left-3 top-1/2 -translate-y-1/2 
                        flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-md
                        ${
                          isQuestionAnswered && isCorrectOption 
                            ? 'bg-green-500 text-white' 
                            : isQuestionAnswered && isWrongSelected
                            ? 'bg-red-500 text-white'
                            : isSelected
                            ? 'bg-orange-500 text-white'
                            : 'bg-white border-2 border-gray-300 text-gray-600'
                        }
                      `}>
                        {optionNumber}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* 선택지 텍스트 */}
                        <span className={`flex-1 leading-relaxed ${
                          isQuestionAnswered && isCorrectOption 
                            ? 'text-green-800 font-semibold' 
                            : isQuestionAnswered && isWrongSelected
                            ? 'text-red-800 line-through'
                            : 'text-gray-800'
                        }`}>
                          {typeof optionText === 'string' 
                            ? renderMathContent(optionText)
                            : optionText
                          }
                        </span>
                        {/* 정답/오답 아이콘 표시 */}
                        {isQuestionAnswered && (
                          <div className="ml-2">
                            {isCorrectOption && (
                              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                            {isWrongSelected && (
                              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* 정답 피드백 패널 - 정답일 때만 표시 */}
              {showResults[currentQuestion] && selectedAnswers[currentQuestion] === question.correctAnswer && (
                <div className="mt-3 p-3 rounded-lg border-l-4 animate-fade-in bg-green-50 border-green-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-bold text-green-800 text-lg">정답입니다! 🎉</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      정답: {question.correctAnswer}번
                    </span>
                  </div>
                  
                  {/* 격려 메시지 */}
                  <div className="text-sm mb-3">
                    <p className="text-green-700">훌륭합니다! 정확하게 이해하고 계시네요.</p>
                  </div>
                </div>
              )}
              
              {/* 해설 보기 버튼 - 답안 제출 후에만 표시 */}
              {showResults[currentQuestion] && !showAIExplanation[currentQuestion] && (
                <div className="mt-3 text-center">
                  <button
                    onClick={() => setShowAIExplanation(prev => ({...prev, [currentQuestion]: true}))}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    AI 해설 보기
                  </button>
                </div>
              )}

              {/* AI 해설 패널 - 버튼 클릭 후에만 표시 */}
              {showResults[currentQuestion] && showAIExplanation[currentQuestion] && (
                <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-l-4 border-purple-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <SparklesIcon className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-800">AI 해설</span>
                    </div>
                    <button
                      onClick={() => setShowAIExplanation(prev => ({...prev, [currentQuestion]: false}))}
                      className="text-purple-500 hover:text-purple-700 p-1 rounded-full hover:bg-purple-100 transition-colors"
                      title="해설 숨기기"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="text-sm text-purple-700 leading-relaxed">
                    {question.explanation ? (
                      <div className="whitespace-pre-line">
                        {renderMathContent(question.explanation)}
                      </div>
                    ) : (
                      <p className="text-purple-600">
                        💡 이 문제에 대한 해설이 준비 중입니다.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </main>

        {/* Footer - Status and Quick Navigation */}
        <footer className="bg-gradient-to-r from-orange-50/90 to-orange-100/90 backdrop-blur-md border-t border-orange-200 px-4 py-4">
          <div className="max-w-6xl mx-auto">

            {/* Quick Navigation */}
            <div className="bg-white/70 backdrop-blur-sm border border-orange-200/50 rounded-xl p-4 shadow-md">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-orange-800 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  빠른 이동
                </h3>
                <div className="flex gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-orange-600 rounded"></div>
                    <span>현재</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded"></div>
                    <span>정답</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-red-500 rounded"></div>
                    <span>오답</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-300 rounded"></div>
                    <span>미답변</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-10 gap-1">
                {diagnosticQuestions.map((_, index) => {
                  const isAnswered = selectedAnswers[index] !== undefined;
                  const isRevealed = showResults[index];
                  const isCorrect = isRevealed && selectedAnswers[index] === diagnosticQuestions[index]?.correctAnswer;
                  const isWrong = isRevealed && selectedAnswers[index] !== diagnosticQuestions[index]?.correctAnswer;
                  const isCurrent = index === currentQuestion;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentQuestion(index);
                        setQuestionStartTime(Date.now());
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`
                        aspect-square rounded flex items-center justify-center text-xs font-bold
                        transition-all duration-200 hover:scale-110 active:scale-95
                        ${isCurrent 
                          ? 'bg-orange-600 text-white' 
                          : isCorrect 
                            ? 'bg-green-500 text-white hover:bg-green-600' 
                            : isWrong
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : isAnswered 
                              ? 'bg-blue-500 text-white hover:bg-blue-600' 
                              : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                        }
                        touch-manipulation
                      `}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>
            </div>

          </div>
        </footer>
      </div>

      {/* 로그인 프롬프트 */}
      {promptConfig.shouldShow && (
        <LoginPromptManager
          userProgress={userMetrics}
          isLoggedIn={isAuthenticated}
          onLogin={(provider) => {
            console.log('Login with:', provider);
            dismissPrompt();
          }}
          onClose={() => dismissPrompt()}
          isDiagnosticMode={true}
        />
      )}
    </div>
  );
};

export default DiagnosticTest;